/**
 * Public showcase card — the shareable POPSTAR profile snapshot
 * (docs/BARRAGE_IMPLEMENTATION_PLAN.md "Replay + showcase"). Reads the
 * Function-written, PUBLIC `cards/{uid}` doc via `services.getUserCard(uid)` and
 * renders the player's headline: display name, level, the sticker they've equipped
 * (slot 1 art), and the single replay they've chosen to feature — with a Watch
 * button that loads the full {@link import('../core/types').RunRecord} and hands it
 * up to the parent's replay player.
 *
 * PUBLIC / ANON-ALLOWED: `getUserCard` and `getReplay` are both anon-readable, so
 * this card works for logged-out viewers (that's the point — it's shareable). The
 * card doc only stores the equipped sticker's *id*, not its art; we try to resolve
 * the art via `services.getStickerCatalog()` for a richer thumbnail, but that read
 * is NAMED-ONLY — if it throws {@link RequiresAccountError} (anon viewer) we simply
 * fall back to a placeholder, so the card never fails to render.
 *
 * ARCHITECTURE: presentational + thin orchestration — screen-local UI state only,
 * talks to the backend ONLY through the {@link GameServices} facade, holds no
 * per-frame state, imports nothing from `game`/Firebase. The parent owns the
 * deterministic replay player via the `onWatch` callback.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AttackFamily, GameMode, RunRecord } from '../core/types';
import { rankForLevel } from '../core/ranks';
import type { GameServices } from '../services/types';
import { RequiresAccountError } from '../services/types';
import { useT } from './i18n';
// The data shape and this component share the name "UserCard"; alias the type so
// the public component export below can keep the natural name.
import type {
  StickerCatalogItem,
  UserCard as UserCardData,
} from '../services/dataModel';

interface UserCardProps {
  /** The data facade. `getUserCard` / `getReplay` are anon-allowed (public). */
  services: GameServices;
  /** Whose public card to show. */
  uid: string;
  /**
   * Invoked when the viewer taps Watch on the featured replay, AFTER its full
   * {@link RunRecord} has been loaded via `services.getReplay(id)`. The parent
   * owns the deterministic replay player; this card only fetches the record.
   */
  onWatch: (record: RunRecord) => void;
  /** Back to the previous screen (parent owns navigation). */
  onBack: () => void;
  /** When true (inside the POPSTAR tab shell) suppress this screen's own back-header. */
  embedded?: boolean;
}

/** Local load lifecycle for the one-shot card fetch. */
type LoadState = 'loading' | 'ready' | 'error';

/** Human label per attack family, for the featured run's "died to" tag. */
const FAMILY_LABEL: Record<AttackFamily, string> = {
  walls: 'Walls',
  lasers: 'Lasers',
  turret: 'Turret',
  caro: 'Caro',
  missiles: 'Missiles',
  bombs: 'Bombs',
};

/** Human label per game mode (multiplayer is a placeholder). */
const MODE_LABEL: Record<GameMode, string> = {
  solo: 'Solo',
  multiplayer: 'Multiplayer',
};

/** mm:ss.S from milliseconds (e.g. 92_400 → "1:32.4"). */
function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0:00.0';
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const tenths = Math.floor((totalSeconds * 10) % 10);
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${tenths}`;
}

export function UserCard({ services, uid, onWatch, onBack, embedded = false }: UserCardProps) {
  const t = useT();
  const [state, setState] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [card, setCard] = useState<UserCardData | null>(null);
  // Resolved art for the equipped sticker, when the catalog read is permitted.
  const [equippedSticker, setEquippedSticker] = useState<StickerCatalogItem | null>(
    null,
  );

  const [watching, setWatching] = useState(false);

  /** Load the public card, then best-effort resolve the equipped sticker's art. */
  const load = useCallback(async () => {
    setState('loading');
    setError(null);
    setEquippedSticker(null);
    try {
      const c = await services.getUserCard(uid);
      setCard(c);
      setState('ready');

      // Best-effort art resolution: the card only carries the sticker id, and the
      // catalog read is NAMED-ONLY. For an anon viewer it throws — that's fine, we
      // just keep the placeholder. The card itself is already rendered.
      if (c.equippedStickerId) {
        try {
          const catalog = await services.getStickerCatalog();
          setEquippedSticker(
            catalog.find((s) => s.id === c.equippedStickerId) ?? null,
          );
        } catch {
          // Anon viewer (RequiresAccountError) or catalog unavailable — ignore.
        }
      }
    } catch (err) {
      setError(
        err instanceof RequiresAccountError
          ? 'This card is not available.'
          : err instanceof Error
            ? err.message
            : 'Could not load this card.',
      );
      setState('error');
    }
  }, [services, uid]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Load the featured replay's full record and hand it to the parent's player. */
  const onWatchStarred = useCallback(async () => {
    const runId = card?.starred?.id;
    if (!runId) return;
    setWatching(true);
    setError(null);
    try {
      const record = await services.getReplay(runId);
      if (!record) {
        setError('That replay is no longer available.');
        return;
      }
      onWatch(record);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load that replay.');
    } finally {
      setWatching(false);
    }
  }, [services, card, onWatch]);

  return (
    <div className={'screen usercard-screen' + (embedded ? ' is-embedded' : '')}>
      <style>{USERCARD_CSS}</style>

      {!embedded && (
        <header className="network-head">
          <button className="btn btn-ghost network-back" type="button" onClick={onBack}>
            ‹ Back
          </button>
          <h1 className="network-title">{t('card.title')}</h1>
        </header>
      )}

      {state === 'loading' && <p className="leaderboard-empty">Loading card…</p>}

      {state === 'error' && (
        <section className="panel">
          <p className="usercard-error">{error}</p>
          <button className="btn btn-ghost" type="button" onClick={() => void load()}>
            Retry
          </button>
        </section>
      )}

      {state === 'ready' && card && (
        <section className="panel usercard">
          {/* Showbiz banner: the glamorous header strip behind the identity. */}
          <div className="usercard-banner" aria-hidden="true">
            <span className="usercard-banner-kicker">{t('card.kicker')}</span>
          </div>

          <div className="usercard-identity">
            <div className="usercard-avatar" aria-hidden="true">
              {card.name.charAt(0).toUpperCase() || '★'}
            </div>
            <div className="usercard-name-block">
              <span className="usercard-name">{card.name}</span>
              <span className="usercard-level">
                <span className="usercard-level-star" aria-hidden="true">
                  ★
                </span>
                Lv {card.level} · {rankForLevel(card.level).name}
              </span>
            </div>
          </div>

          {/* Equipped sticker — framed like a collectible, with a rarity ring. */}
          <div className="usercard-equip">
            <span className="usercard-section-label">{t('card.equippedSticker')}</span>
            <div className="usercard-equip-row">
              <div
                className={`usercard-equipped${
                  equippedSticker ? ` rarity-${equippedSticker.rarity}` : ''
                }`}
              >
                {card.equippedStickerId ? (
                  equippedSticker && equippedSticker.imageUrl ? (
                    <img
                      className="usercard-sticker-art"
                      src={equippedSticker.imageUrl}
                      alt={equippedSticker.title}
                      draggable={false}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className="usercard-sticker-ph" aria-hidden="true">
                      ◆
                    </span>
                  )
                ) : (
                  <span className="usercard-sticker-ph muted" aria-hidden="true">
                    ◇
                  </span>
                )}
              </div>
              <div className="usercard-equip-meta">
                {card.equippedStickerId ? (
                  <>
                    <span className="usercard-equip-title">
                      {equippedSticker?.title ?? 'Equipped sticker'}
                    </span>
                    {equippedSticker && (
                      <span className="usercard-equip-rarity">
                        {equippedSticker.rarity}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="usercard-sticker-empty muted-line">
                    {t('card.noEquipped')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Featured (starred) replay — the headline, shareable moment. */}
          <div className="usercard-featured">
            <span className="usercard-section-label">
              <span className="usercard-star" aria-hidden="true">
                ★
              </span>
              {t('card.featuredRun')}
            </span>
            {card.starred ? (
              <div className="usercard-run">
                <div className="usercard-run-meta">
                  <span className="usercard-run-score">
                    {card.starred.score.toLocaleString()}
                  </span>
                  <span className="usercard-run-tags">
                    <span className="usercard-run-tag">
                      {MODE_LABEL[card.starred.mode]}
                    </span>
                    <span className="usercard-run-tag">
                      {formatDuration(card.starred.durationMs)}
                    </span>
                    {card.starred.deathFamily && (
                      <span className="usercard-run-tag usercard-run-tag-death">
                        {t('family.' + card.starred.deathFamily, FAMILY_LABEL[card.starred.deathFamily])}
                      </span>
                    )}
                  </span>
                </div>
                <button
                  className="btn btn-primary usercard-watch"
                  type="button"
                  disabled={watching}
                  onClick={() => void onWatchStarred()}
                >
                  {watching ? t('common.loading') : t('common.watch')}
                </button>
              </div>
            ) : (
              <p className="usercard-no-run muted-line">
                {card.name} {t('card.noRunSuffix')}
              </p>
            )}
          </div>

          {error && (
            <p className="usercard-error" role="alert">
              {error}
            </p>
          )}
        </section>
      )}
    </div>
  );
}

/**
 * Screen-scoped styles (nested under `.usercard-screen` so nothing leaks). Shared
 * primitives (`.screen`, `.panel`, `.btn`, `.network-head`, `.network-title`,
 * `.muted-line`, `.leaderboard-empty`, CSS vars) come from src/index.css; only the
 * card-specific layout lives here.
 */
const USERCARD_CSS = `
.usercard-screen .usercard {
  position: relative;
  width: min(440px, 92vw);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  /* lift past the .panel default padding so the banner can bleed to the edges */
  padding: 0 0 var(--space-5);
  overflow: hidden;
}

/* ---- Showbiz banner: a magenta→gold wash that bleeds to the card edges ---- */
.usercard-screen .usercard-banner {
  position: relative;
  height: 64px;
  display: flex;
  align-items: flex-end;
  padding: 0 var(--space-5) 10px;
  background: linear-gradient(
    120deg,
    var(--accent) 0%,
    var(--accent-2) 55%,
    var(--gold) 130%
  );
}
.usercard-screen .usercard-banner::after {
  /* a soft glassy sheen across the banner */
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.18),
    rgba(255, 255, 255, 0) 60%
  );
  pointer-events: none;
}
.usercard-screen .usercard-banner-kicker {
  position: relative;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--accent-ink);
  opacity: 0.85;
}

/* ---- Identity: avatar overlapping the banner + name/level ---- */
.usercard-screen .usercard-identity {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: 0 var(--space-5);
  margin-top: -34px;
}
.usercard-screen .usercard-avatar {
  width: 72px;
  height: 72px;
  flex: 0 0 auto;
  border-radius: var(--radius-lg);
  display: grid;
  place-items: center;
  /* gradient + glow injected by index.css's .usercard-avatar rule */
  color: #fff;
  font-size: 32px;
  font-weight: 900;
  /* a glassy ring so the avatar reads as lifted off the banner */
  border: 2px solid var(--glass-border-strong);
  outline: 3px solid var(--panel-strong);
}
.usercard-screen .usercard-name-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1 1 auto;
  padding-top: 30px;
}
.usercard-screen .usercard-name {
  font-size: 22px;
  font-weight: 800;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.usercard-screen .usercard-level {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  color: var(--gold);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: rgba(255, 209, 102, 0.12);
  border: 1px solid rgba(255, 209, 102, 0.3);
  border-radius: var(--radius-pill);
  padding: 3px 10px;
}
.usercard-screen .usercard-level-star {
  font-size: 11px;
  line-height: 1;
}

/* ---- Section labels shared by the equipped + featured blocks ---- */
.usercard-screen .usercard-section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 10px;
}
.usercard-screen .usercard-star {
  color: var(--gold);
  font-size: 13px;
  line-height: 1;
}

/* ---- Equipped sticker (collectible frame + rarity ring) ---- */
.usercard-screen .usercard-equip,
.usercard-screen .usercard-featured {
  margin: 0 var(--space-5);
  padding: var(--space-4);
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius);
}
.usercard-screen .usercard-equip-row {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  min-width: 0;
}
.usercard-screen .usercard-equipped {
  width: 64px;
  height: 64px;
  flex: 0 0 auto;
  border-radius: 14px;
  background: var(--panel-strong);
  display: grid;
  place-items: center;
  overflow: hidden;
  /* default ring; replaced by .rarity-* when the sticker art resolves */
  box-shadow: inset 0 0 0 2px var(--glass-border);
}
.usercard-screen .usercard-sticker-art {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.usercard-screen .usercard-sticker-ph {
  font-size: 28px;
  color: var(--accent-soft);
}
.usercard-screen .usercard-sticker-ph.muted {
  color: var(--muted);
  opacity: 0.6;
}
.usercard-screen .usercard-equip-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1 1 auto;
}
.usercard-screen .usercard-equip-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.usercard-screen .usercard-equip-rarity {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--accent-soft);
}
.usercard-screen .usercard-sticker-empty {
  font-size: 13px;
}

/* ---- Featured run ---- */
.usercard-screen .usercard-run {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}
.usercard-screen .usercard-run-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}
.usercard-screen .usercard-run-score {
  font-size: 32px;
  font-weight: 900;
  color: var(--text);
  font-variant-numeric: tabular-nums;
  line-height: 1;
  background: linear-gradient(96deg, var(--accent-soft), var(--gold));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 2px 12px var(--accent-glow));
}
.usercard-screen .usercard-run-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.usercard-screen .usercard-run-tag {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-dim);
  background: var(--panel-strong);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  padding: 3px 10px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.usercard-screen .usercard-run-tag-death {
  color: var(--accent-2);
  border-color: var(--accent-2);
}
.usercard-screen .usercard-watch {
  flex: 0 0 auto;
  white-space: nowrap;
}
.usercard-screen .usercard-no-run {
  margin: 0;
  font-size: 14px;
}
.usercard-screen .usercard-error {
  margin: 0 var(--space-5);
  color: var(--danger);
  font-size: 13px;
  text-align: center;
}

@media (max-width: 380px) {
  .usercard-screen .usercard-run {
    flex-direction: column;
    align-items: stretch;
  }
  .usercard-screen .usercard-watch {
    width: 100%;
  }
}
`;
